import React, { useState, useEffect, useRef } from 'react';
import { useLoading } from '../contexts/LoadingContext';
import { useError } from '../contexts/ErrorContext';
import { useAuth } from '../contexts/AuthContext';

interface Staff {
  id: string;
  name: string;
  status: 'Hoạt động' | 'Đã nghỉ';
  role: string;
}

export const MakeupCheckInOutPage: React.FC = () => {
  const { setLoading } = useLoading();
  const { error, setError, clearError } = useError();
  const { user } = useAuth();
  
  const [staff, setStaff] = useState<Staff[]>([]);
  const [selectedStaffId, setSelectedStaffId] = useState<string>('');
  const [checkType, setCheckType] = useState<'check_in' | 'check_out'>('check_in');
  const [staffSearch, setStaffSearch] = useState('');
  const [showStaffDropdown, setShowStaffDropdown] = useState(false);
  const [location, setLocation] = useState<{ latitude: number; longitude: number; address?: string } | null>(null);
  const [locationError, setLocationError] = useState<string>('');
  const [checkTime, setCheckTime] = useState<string>('');
  const [reason, setReason] = useState<string>('');
  const [dateParts, setDateParts] = useState<{ day: string; month: string; year: string; hour: string; minute: string }>(() => {
    const now = new Date();
    const day = String(now.getDate()).padStart(2, '0');
    const month = String(now.getMonth() + 1).padStart(2, '0');
    const year = String(now.getFullYear());
    const hours = String(now.getHours()).padStart(2, '0');
    const minutes = String(now.getMinutes()).padStart(2, '0');
    return { day, month, year, hour: hours, minute: minutes };
  });
  
  const staffSearchRef = useRef<HTMLDivElement>(null);
  const checkIsMobile = () => window.innerWidth < 768 || (window.innerHeight <= 500 && window.innerWidth <= 950);
  const [isMobile, setIsMobile] = useState(checkIsMobile());

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(checkIsMobile());
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Load staff list and restore from localStorage or logged-in user
  useEffect(() => {
    const loadStaff = async () => {
      try {
        const response = await fetch('/api/staff?all=true');
        const data = await response.json();
        if (data.success) {
          setStaff(data.data);
          
          // Priority 1: Use logged-in user's staff_id if available
          if (user?.id) {
            const loggedInStaff = data.data.find((s: Staff) => s.id === user.id && s.status === 'Hoạt động');
            if (loggedInStaff) {
              setSelectedStaffId(user.id);
              setStaffSearch(loggedInStaff.name);
            }
          } else {
            // Priority 2: Restore from localStorage
            try {
              const savedStaffId = localStorage.getItem('checkinout_last_staff_id');
              if (savedStaffId) {
                const staffExists = data.data.some((s: Staff) => s.id === savedStaffId && s.status === 'Hoạt động');
                if (staffExists) {
                  setSelectedStaffId(savedStaffId);
                  const staffMember = data.data.find((s: Staff) => s.id === savedStaffId);
                  if (staffMember) {
                    setStaffSearch(staffMember.name);
                  }
                }
              }
            } catch (err) {
              console.error('Error restoring from localStorage:', err);
            }
          }
        }
      } catch (err) {
        console.error('Error loading staff:', err);
      }
    };
    loadStaff();
  }, [user]);

  // Get location when component mounts
  useEffect(() => {
    getCurrentLocation();
  }, []);

  // Update checkTime when dateParts change
  useEffect(() => {
    const { day, month, year, hour, minute } = dateParts;
    if (day && month && year && hour && minute) {
      const isoDateTime = `${year}-${month}-${day}T${hour.padStart(2, '0')}:${minute.padStart(2, '0')}`;
      setCheckTime(isoDateTime);
    }
  }, [dateParts]);

  const getCurrentLocation = () => {
    setLocationError('');
    if (!navigator.geolocation) {
      setLocationError('Trình duyệt không hỗ trợ định vị');
      return;
    }

    setLoading(true, 'Đang lấy vị trí...');
    navigator.geolocation.getCurrentPosition(
      async (position) => {
        const { latitude, longitude } = position.coords;
        
        // Get address from backend API
        try {
          const response = await fetch('/api/geocode/reverse', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ latitude, longitude })
          });
          
          const data = await response.json();
          if (data.success && data.address) {
            setLocation({ latitude, longitude, address: data.address });
          } else {
            setLocation({ latitude, longitude, address: `${latitude.toFixed(6)}, ${longitude.toFixed(6)}` });
          }
        } catch (err) {
          console.error('Error getting address:', err);
          setLocation({ latitude, longitude, address: `${latitude.toFixed(6)}, ${longitude.toFixed(6)}` });
        }
        setLoading(false);
      },
      (error) => {
        setLoading(false);
        let errorMessage = 'Không thể lấy vị trí';
        switch (error.code) {
          case error.PERMISSION_DENIED:
            errorMessage = 'Bạn đã từ chối quyền truy cập vị trí';
            break;
          case error.POSITION_UNAVAILABLE:
            errorMessage = 'Thông tin vị trí không khả dụng';
            break;
          case error.TIMEOUT:
            errorMessage = 'Hết thời gian chờ lấy vị trí';
            break;
        }
        setLocationError(errorMessage);
      },
      {
        enableHighAccuracy: true,
        timeout: 10000,
        maximumAge: 60000
      }
    );
  };

  // Filter staff based on search
  const filteredStaffForSearch = staff.filter(s => {
    if (s.status !== 'Hoạt động') return false;
    if (!staffSearch.trim()) return true;
    const searchLower = staffSearch.toLowerCase();
    return s.name.toLowerCase().includes(searchLower) || 
           (s.role && s.role.toLowerCase().includes(searchLower));
  });

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (staffSearchRef.current && !staffSearchRef.current.contains(event.target as Node)) {
        setShowStaffDropdown(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    clearError();

    if (!selectedStaffId) {
      setError('Vui lòng chọn nhân viên');
      return;
    }

    if (!location) {
      setError('Vui lòng cho phép truy cập vị trí để tiếp tục');
      return;
    }

    if (!location.address || location.address.trim() === '') {
      setError('Không thể lấy địa chỉ. Vui lòng thử lại.');
      return;
    }

    if (!checkTime) {
      setError('Vui lòng chọn thời gian điểm danh');
      return;
    }

    if (!reason || reason.trim() === '') {
      setError('Vui lòng nhập lý do điểm danh bù');
      return;
    }

    try {
      setLoading(true, 'Đang gửi yêu cầu điểm danh bù...');
      
      const response = await fetch('/api/checkinout/compensation', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          staffId: selectedStaffId,
          checkType,
          checkTime,
          latitude: location.latitude,
          longitude: location.longitude,
          address: location.address,
          reason: reason.trim()
        })
      });

      const data = await response.json();
      
      if (data.success) {
        alert(data.message || 'Đã gửi yêu cầu điểm danh bù thành công!');
        // Reset form
        setReason('');
        const now = new Date();
        const day = String(now.getDate()).padStart(2, '0');
        const month = String(now.getMonth() + 1).padStart(2, '0');
        const year = String(now.getFullYear());
        const hours = String(now.getHours()).padStart(2, '0');
        const minutes = String(now.getMinutes()).padStart(2, '0');
        setDateParts({ day, month, year, hour: hours, minute: minutes });
      } else {
        setError(data.error || 'Có lỗi xảy ra');
      }
    } catch (err) {
      console.error('Error submitting compensation request:', err);
      setError('Có lỗi xảy ra khi gửi yêu cầu');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="makeup-checkinout-page" style={{ 
      padding: isMobile ? '10px' : '20px', 
      maxWidth: '800px', 
      margin: '0 auto',
      width: '100%'
    }}>
      <div className="container">
        <div className="header" style={{ marginBottom: isMobile ? '20px' : '30px' }}>
          <h1 style={{ fontSize: isMobile ? '20px' : '24px' }}>
            <i className="fas fa-edit"></i> Điểm Danh Bù
          </h1>
          <p style={{ color: '#666', fontSize: '14px', marginTop: '8px' }}>
            Điền form để yêu cầu điểm danh bù. Yêu cầu sẽ được admin xét duyệt.
          </p>
        </div>

        <form onSubmit={handleSubmit} style={{ 
          background: 'white', 
          padding: isMobile ? '15px' : '30px', 
          borderRadius: '8px', 
          boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
          width: '100%'
        }}>
          {/* Staff Selection */}
          <div className="form-group" style={{ marginBottom: '25px' }}>
            <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500', color: '#333' }}>
              <i className="fas fa-user"></i> Chọn Nhân Viên <span style={{ color: 'red' }}>*</span>
            </label>
            <div 
              ref={staffSearchRef}
              style={{ position: 'relative', width: '100%' }}
            >
              <div style={{ position: 'relative' }}>
                <input
                  type="text"
                  placeholder="Tìm kiếm nhân viên (tên hoặc vai trò)..."
                  value={staffSearch}
                  onChange={(e) => {
                    setStaffSearch(e.target.value);
                    setShowStaffDropdown(true);
                  }}
                  onFocus={() => setShowStaffDropdown(true)}
                  style={{
                    width: '100%',
                    padding: '12px 40px 12px 15px',
                    border: '1px solid #ddd',
                    borderRadius: '4px',
                    fontSize: '14px'
                  }}
                />
                <i 
                  className="fas fa-search" 
                  style={{
                    position: 'absolute',
                    right: '15px',
                    top: '50%',
                    transform: 'translateY(-50%)',
                    color: '#999',
                    pointerEvents: 'none'
                  }}
                />
              </div>
              
              {showStaffDropdown && (
                <div
                  style={{
                    position: 'absolute',
                    top: '100%',
                    left: 0,
                    right: 0,
                    background: 'white',
                    border: '1px solid #ddd',
                    borderTop: 'none',
                    borderRadius: '0 0 4px 4px',
                    maxHeight: '300px',
                    overflowY: 'auto',
                    zIndex: 1000,
                    boxShadow: '0 4px 6px rgba(0,0,0,0.1)'
                  }}
                >
                  {filteredStaffForSearch.length > 0 ? (
                    filteredStaffForSearch.map(s => (
                      <div
                        key={s.id}
                        onClick={() => {
                          setSelectedStaffId(s.id);
                          setStaffSearch(s.name);
                          setShowStaffDropdown(false);
                        }}
                        style={{
                          padding: '12px 15px',
                          cursor: 'pointer',
                          borderBottom: '1px solid #f0f0f0',
                          backgroundColor: selectedStaffId === s.id ? '#e3f2fd' : 'white',
                          transition: 'background-color 0.2s'
                        }}
                        onMouseEnter={(e) => {
                          if (selectedStaffId !== s.id) {
                            e.currentTarget.style.backgroundColor = '#f5f5f5';
                          }
                        }}
                        onMouseLeave={(e) => {
                          if (selectedStaffId !== s.id) {
                            e.currentTarget.style.backgroundColor = 'white';
                          }
                        }}
                      >
                        <div style={{ fontWeight: '500', color: '#333' }}>
                          {s.name}
                        </div>
                        {s.role && (
                          <div style={{ fontSize: '12px', color: '#666', marginTop: '4px' }}>
                            {s.role}
                          </div>
                        )}
                      </div>
                    ))
                  ) : (
                    <div style={{ padding: '15px', textAlign: 'center', color: '#999' }}>
                      Không tìm thấy nhân viên
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>

          {/* Check Type Selection */}
          <div className="form-group" style={{ marginBottom: '25px' }}>
            <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500', color: '#333', fontSize: isMobile ? '14px' : '16px' }}>
              <i className="fas fa-toggle-on"></i> Loại Điểm Danh <span style={{ color: 'red' }}>*</span>
            </label>
            <div style={{ display: 'flex', gap: isMobile ? '10px' : '15px', flexDirection: isMobile ? 'column' : 'row' }}>
              <label style={{ 
                flex: 1, 
                padding: isMobile ? '12px' : '15px', 
                border: `2px solid ${checkType === 'check_in' ? '#28a745' : '#ddd'}`, 
                borderRadius: '8px', 
                cursor: 'pointer',
                backgroundColor: checkType === 'check_in' ? '#e8f5e9' : 'white',
                transition: 'all 0.2s',
                fontSize: isMobile ? '14px' : '16px'
              }}>
                <input
                  type="radio"
                  name="checkType"
                  value="check_in"
                  checked={checkType === 'check_in'}
                  onChange={(e) => setCheckType(e.target.value as 'check_in' | 'check_out')}
                  style={{ marginRight: '8px' }}
                />
                <i className="fas fa-sign-in-alt" style={{ color: '#28a745', marginRight: '8px' }}></i>
                Check In
              </label>
              <label style={{ 
                flex: 1, 
                padding: isMobile ? '12px' : '15px', 
                border: `2px solid ${checkType === 'check_out' ? '#dc3545' : '#ddd'}`, 
                borderRadius: '8px', 
                cursor: 'pointer',
                backgroundColor: checkType === 'check_out' ? '#ffebee' : 'white',
                transition: 'all 0.2s',
                fontSize: isMobile ? '14px' : '16px'
              }}>
                <input
                  type="radio"
                  name="checkType"
                  value="check_out"
                  checked={checkType === 'check_out'}
                  onChange={(e) => setCheckType(e.target.value as 'check_in' | 'check_out')}
                  style={{ marginRight: '8px' }}
                />
                <i className="fas fa-sign-out-alt" style={{ color: '#dc3545', marginRight: '8px' }}></i>
                Check Out
              </label>
            </div>
          </div>

          {/* Date Time Selection */}
          <div className="form-group" style={{ marginBottom: '25px' }}>
            <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500', color: '#333' }}>
              <i className="fas fa-calendar-alt"></i> Thời Gian Điểm Danh <span style={{ color: 'red' }}>*</span>
            </label>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                <input
                  type="text"
                  value={dateParts.day}
                  onChange={(e) => {
                    const value = e.target.value.replace(/\D/g, '').slice(0, 2);
                    setDateParts({ ...dateParts, day: value });
                  }}
                  placeholder="DD"
                  style={{
                    padding: '12px',
                    border: '1px solid #ddd',
                    borderRadius: '4px',
                    fontSize: '14px',
                    width: '60px',
                    textAlign: 'center'
                  }}
                  required
                />
                <span style={{ fontSize: '16px', fontWeight: '500' }}>-</span>
                <input
                  type="text"
                  value={dateParts.month}
                  onChange={(e) => {
                    const value = e.target.value.replace(/\D/g, '').slice(0, 2);
                    setDateParts({ ...dateParts, month: value });
                  }}
                  placeholder="MM"
                  style={{
                    padding: '12px',
                    border: '1px solid #ddd',
                    borderRadius: '4px',
                    fontSize: '14px',
                    width: '60px',
                    textAlign: 'center'
                  }}
                  required
                />
                <span style={{ fontSize: '16px', fontWeight: '500' }}>-</span>
                <input
                  type="text"
                  value={dateParts.year}
                  onChange={(e) => {
                    const value = e.target.value.replace(/\D/g, '').slice(0, 4);
                    setDateParts({ ...dateParts, year: value });
                  }}
                  placeholder="YYYY"
                  style={{
                    padding: '12px',
                    border: '1px solid #ddd',
                    borderRadius: '4px',
                    fontSize: '14px',
                    width: '80px',
                    textAlign: 'center'
                  }}
                  required
                />
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: '4px', marginLeft: '8px' }}>
                <input
                  type="text"
                  value={dateParts.hour}
                  onChange={(e) => {
                    const value = e.target.value.replace(/\D/g, '').slice(0, 2);
                    setDateParts({ ...dateParts, hour: value });
                  }}
                  placeholder="HH"
                  style={{
                    padding: '12px',
                    border: '1px solid #ddd',
                    borderRadius: '4px',
                    fontSize: '14px',
                    width: '60px',
                    textAlign: 'center'
                  }}
                  required
                />
                <span style={{ fontSize: '16px', fontWeight: '500' }}>:</span>
                <input
                  type="text"
                  value={dateParts.minute}
                  onChange={(e) => {
                    const value = e.target.value.replace(/\D/g, '').slice(0, 2);
                    setDateParts({ ...dateParts, minute: value });
                  }}
                  placeholder="mm"
                  style={{
                    padding: '12px',
                    border: '1px solid #ddd',
                    borderRadius: '4px',
                    fontSize: '14px',
                    width: '60px',
                    textAlign: 'center'
                  }}
                  required
                />
              </div>
            </div>
            <div style={{ fontSize: '12px', color: '#666', marginTop: '4px' }}>
              Định dạng: DD-MM-YYYY HH:mm (24 giờ)
            </div>
          </div>

          {/* Reason */}
          <div className="form-group" style={{ marginBottom: '25px' }}>
            <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500', color: '#333' }}>
              <i className="fas fa-comment-alt"></i> Lý Do Điểm Danh Bù <span style={{ color: 'red' }}>*</span>
            </label>
            <textarea
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder="Nhập lý do tại sao bạn cần điểm danh bù..."
              rows={4}
              style={{
                width: '100%',
                padding: '12px',
                border: '1px solid #ddd',
                borderRadius: '4px',
                fontSize: '14px',
                fontFamily: 'inherit',
                resize: 'vertical'
              }}
              required
            />
          </div>

          {/* Location Display */}
          <div className="form-group" style={{ marginBottom: '25px' }}>
            <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500', color: '#333' }}>
              <i className="fas fa-map-marker-alt"></i> Vị Trí
            </label>
            {locationError ? (
              <div style={{
                padding: '12px',
                background: '#f8d7da',
                border: '1px solid #f5c6cb',
                borderRadius: '4px',
                color: '#721c24'
              }}>
                <i className="fas fa-exclamation-triangle"></i> {locationError}
                <button
                  type="button"
                  onClick={getCurrentLocation}
                  style={{
                    marginLeft: '10px',
                    padding: '5px 10px',
                    background: '#dc3545',
                    color: 'white',
                    border: 'none',
                    borderRadius: '4px',
                    cursor: 'pointer'
                  }}
                >
                  Thử lại
                </button>
              </div>
            ) : location ? (
              <div style={{
                padding: '12px',
                background: '#d4edda',
                border: '1px solid #c3e6cb',
                borderRadius: '4px',
                color: '#155724'
              }}>
                <i className="fas fa-check-circle"></i> Đã lấy vị trí thành công
                <div style={{ marginTop: '8px', fontSize: '12px' }}>
                  {location.address && (
                    <div style={{ marginTop: '4px' }}>Địa chỉ: {location.address}</div>
                  )}
                </div>
                <button
                  type="button"
                  onClick={getCurrentLocation}
                  style={{
                    marginTop: '8px',
                    padding: '5px 10px',
                    background: '#28a745',
                    color: 'white',
                    border: 'none',
                    borderRadius: '4px',
                    cursor: 'pointer',
                    fontSize: '12px'
                  }}
                >
                  <i className="fas fa-sync-alt"></i> Làm mới vị trí
                </button>
              </div>
            ) : (
              <div style={{
                padding: '12px',
                background: '#e2e3e5',
                border: '1px solid #d6d8db',
                borderRadius: '4px',
                color: '#383d41'
              }}>
                <i className="fas fa-info-circle"></i> Đang chờ lấy vị trí...
              </div>
            )}
          </div>

          {/* Error Message */}
          {error && (
            <div style={{
              padding: '12px',
              background: '#f8d7da',
              border: '1px solid #f5c6cb',
              borderRadius: '4px',
              marginBottom: '20px',
              color: '#721c24'
            }}>
              <i className="fas fa-exclamation-circle"></i> {error}
            </div>
          )}

          {/* Submit Button */}
          <button
            type="submit"
            disabled={!selectedStaffId || !location || !!locationError || !location.address || !checkTime || !reason.trim()}
            style={{
              width: '100%',
              padding: isMobile ? '12px' : '15px',
              background: '#8d2a2c',
              color: 'white',
              border: 'none',
              borderRadius: '8px',
              fontSize: isMobile ? '14px' : '16px',
              fontWeight: '600',
              cursor: (!selectedStaffId || !location || !!locationError || !checkTime || !reason.trim()) ? 'not-allowed' : 'pointer',
              opacity: (!selectedStaffId || !location || !!locationError || !checkTime || !reason.trim()) ? 0.6 : 1,
              transition: 'opacity 0.2s'
            }}
          >
            <i className="fas fa-paper-plane"></i>
            {' '}
            Gửi Yêu Cầu Điểm Danh Bù
          </button>
        </form>
      </div>
    </div>
  );
};
